<template>
    <div v-if="loading" class="flex flex-column justify-content-center align-items-center">
        <h4 class="text-center">Loading...</h4>
        <ScaleLoader class="m-auto" />
    </div>

    <div v-if="!loading" class="card">
        <div class="grid">
            <div class="form-field col-6">
                <div class="grid">
                    <div class="form-field col-6">
                        <label for="name">Dashboard Name</label>
                        <InputText maxlength = "255" v-model="dashboardModel.name" id="name" placeholder="Enter dashboard name" />
                    </div>
                    <div class="form-field col-6">
                        <label for="code">Code</label>
                        <InputText maxlength = "255" v-model="dashboardModel.code" id="code" placeholder="Enter dashboard code" />
                    </div>
                </div>
                <div class="grid">
                    <div class="form-field col-6">
                        <label for="order">Order</label>
                        <InputText  maxlength = "255" v-model="dashboardModel.order" id="order" placeholder="Enter dashboard order" />
                    </div>
                    <div class="form-field col-6">
                        <label for="default">Default</label>
                        <Dropdown
                            id="default"
                            v-model="dashboardModel.default"
                            :options="defaultOption"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Make it the default"
                            class="w-full mr-2"
                            :class="{ 'p-invalid': props.isInvalid }"
                            showClear
                        />
                    </div>
                </div>
            </div>
            <div class="form-field col-6">
                <label for="description">Description:</label>
                <Textarea rows="8" id="description" v-model="dashboardModel.description" />
            </div>
        </div>

        <div class="mt-3">

            <h5>Manage Metrics and counters</h5>
            <edit-metrics
                :metrics="dashboardModel.details"
                @add-item="addItem"
                @updateMetricsList="updateMetricsList"
                :metrics-available-list="metricsAvailableList"
                :user-group-available-list="userGroupAvailableList"
                :specialUserGroupAvailableList="specialUserGroupAvailableList"
            />
        </div>

        <div class="card flex justify-content-between">
            <div class="vertical-align-middle">
                <p v-if="!isValid" class=" mt-2">
                    Please, fill the `name` field and add at least one `metric`.
                </p>
            </div>
            <div class="flex justify-content-end">
                <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="onCancel" />
                <Button label="Submit" icon="pi pi-check" class="ml-2 p-button-primary" @click="onSubmit" :disabled="!isValid || savingInProgress" />
            </div>
        </div>



    </div>
</template>

<script setup>

import { computed, defineProps, onBeforeMount, ref } from 'vue';
import { useRouter } from 'vue-router';
import GlobalRequest from '@/api/global-request';
import store from '@/store';
import { showToast } from '@/helpers/toast-handler';
import EditMetrics from '@/products/pha/modules/dashboard/components/metric/EditMetrics.vue';

const router = useRouter();

const props = defineProps({
    id: {
        type: [String, Number],
        required: false,
    },
});

const company_id = computed(() => {
    return store.getters['auth/userCompanyId']?.id;
});

const dashboardModel = ref({
    name: null,
    description: null,
    code: null,
    order: null,
    default: 0,
    id: null,
    company_id: null,
    details: [],
})

const isValid = computed(() => {

    if (dashboardModel.value.name == null || dashboardModel.value.name.length < 1) {
        return false;
    }

    if (dashboardModel.value.code == null || dashboardModel.value.code.length < 1) {
        return false;
    }

    if (dashboardModel.value.details == null || dashboardModel.value.details.length < 1) {
        return false;
    }

    if (dashboardModel.value.order == null || dashboardModel.value.order.length < 1) {
        return false;
    }

    return true;
})


const loading = ref(true);

const updateMetricsList = (list) => {
    dashboardModel.value.details = list;
}

const addItem = (detail) => {
    let order = 1;

    // select max order
    dashboardModel.value.details.forEach((item) => {
        if (item.order > order) {
            order = item.order;
        }
    });

    let displayName = '';

    if (detail.type === 'metric') {
        metricsAvailableList.value.forEach((metric) => {
            if (metric.id === detail.id) {
                displayName = metric.name
            }
        })
    } else if (detail.type === 'userGroup') {
        userGroupAvailableList.value.forEach((metric) => {
            if (metric.id === detail.id) {
                displayName = metric.name
            }
        })
    } else if (detail.type === 'specialUserGroup') {
        specialUserGroupAvailableList.value.forEach((metric) => {
            if (metric.id === detail.id) {
                displayName = metric.name
            }
        })
    }

    order++;

    let stereotype = ''

    if (detail.type === 'metric') {
        stereotype = 'metric'
    } else if (detail.type === 'userGroup') {
        stereotype = 'user_group'
    } else if (detail.type === 'specialUserGroup') {
        stereotype = 'special_user_group'
    }

    dashboardModel.value.details.push({
        id: detail.id,
        order: order,
        name: displayName,
        stereotype: stereotype,
    })
}

const onCancel = () => {
    router.push({ name: 'ManageDashboard' });
};

const onSubmit = () => {
    saveDashboard(dashboardModel.value);
}

const defaultOption = [
    {
        label: 'Yes',
        value: 1
    },
    {
        label: 'No',
        value: 0
    },
];

const savingInProgress = ref(false);

const saveDashboard = async (dashboard) => {
    let request = {
        id: dashboard.id,
        company_id: company_id.value,
        name: dashboard.name,
        default: dashboard.default,
        code: dashboard.code,
        order: dashboard.order,
        description: dashboard.description,
        details: [],
    }

    dashboard.details.forEach((item) => {
        if (item.stereotype === 'user_group') {
            request.details.push({
                usergroups_id: item.id,
                order: item.order
            })
        } else if (item.stereotype === 'metric') {
            request.details.push({
                metric_id: item.id,
                order: item.order
            })
        } else if (item.stereotype === 'special_user_group') {
            request.details.push({
                special_usergroups_id: item.id,
                order: item.order
            })
        }
    })

    dashboard.company_id = company_id.value;

    savingInProgress.value = true;

    if (dashboard.id === null) {
        const dashBoardCreateUrl = "/v2/datasource/dashboard";

        return await GlobalRequest.postRequest(dashBoardCreateUrl, request).then((res) => {
            savingInProgress.value = false;
            if (res.statusCode === 201) {
                router.push({ name: 'ManageDashboard' });
            } else {
                showToast(res, 'Something went wrong');
            }
        })
    } else {
        const dashBoardAmendUrl = "/v2/datasource/dashboard/"+request.id;

        return await GlobalRequest.patchRequest(dashBoardAmendUrl, request).then((res) => {
            savingInProgress.value = false;
            if (res.statusCode === 200) {
                router.push({ name: 'ManageDashboard' });
            } else {
                showToast(res, 'Something went wrong');
            }
        })
    }
}

const metricsAvailableList = ref([])
const userGroupAvailableList = ref([])
const specialUserGroupAvailableList = ref([])

const loadDashboardDetails = async (dashboard_id) => {
    const dashBoardUrl = "/v2/datasource/dashboard/" + dashboard_id;

    loading.value = true;

    GlobalRequest.getRequest(dashBoardUrl, {
        params: {
            company_id: company_id.value,
        },
    }).then((res) => {
        if (res.statusCode === 200) {
            const data = res.data;

            dashboardModel.value.code = data.code;
            dashboardModel.value.default = data.default;
            dashboardModel.value.name = data.name;
            dashboardModel.value.description = data.description;
            dashboardModel.value.order = data.order;
            dashboardModel.value.id = data.id;
            dashboardModel.value.company_id = data.company_id;

            data.details.forEach( detail => {

                if (Object.hasOwn(detail, 'metric') && detail.metric !== null) {
                    dashboardModel.value.details.push({
                        id: detail.metric.id,
                        order: detail.order,
                        name: detail.metric.name,
                        type: detail.metric.type,
                        stereotype: 'metric',
                    })
                } else if (Object.hasOwn(detail, 'user_group') && detail.user_group !== null) {
                    dashboardModel.value.details.push({
                        id: detail.user_group.id,
                        order: detail.order,
                        name: detail.user_group.name,
                        type: detail.user_group.type,
                        stereotype: 'user_group',
                    })
                } else if (Object.hasOwn(detail, 'special_user_group')) {

                    dashboardModel.value.details.push({
                        id: detail.special_user_group.id,
                        order: detail.order,
                        name: detail.special_user_group.name,
                        type: detail.special_user_group.type,
                        stereotype: 'special_user_group',
                    })
                }
            } )

            loading.value = false;
        } else {
            showToast(res, 'Something went wrong');
        }
    }).catch((err) => {
        showToast(err, 'Something went wrong')
    });
}

onBeforeMount(async () => {
    GlobalRequest.getRequest('/v2/datasource/metric', {
        params: {
            company_id: company_id.value,
            paginate: false,
            execute: false,
        },
    }).then((res) => {
        if (res.statusCode === 200) {
            res.data.forEach(metric => {
                metricsAvailableList.value.push(metric.metric)
            })
        }
    }).catch((err) => {
        showToast(err, 'Something went wrong');
    })

    GlobalRequest.getRequest('/v2/datasource/users', {
        params: {
            company_id: company_id.value,
            paginate: false,
            execute: false,
        },
    }).then((res) => {
        if (res.statusCode === 200) {
            res.data.forEach(metric => {
                userGroupAvailableList.value.push(metric)
            })
        }
    }).catch((err) => {
        showToast(err, 'Something went wrong');
        console.log('err', err)
    })

    GlobalRequest.getRequest('/v2/datasource/special/user/group', {
        params: {
            company_id: company_id.value,
            paginate: false,
            execute: false,
        },
    }).then((res) => {
        if (res.statusCode === 200) {
            res.data.forEach(specialUserGroup => {
                specialUserGroupAvailableList.value.push(specialUserGroup)
            })
        }
    }).catch((err) => {
        showToast(err, 'Something went wrong');
        console.error('err', err)
    })

    if (props.id !== undefined) {
        await loadDashboardDetails(props.id);
    } else {
        loading.value = false;
    }

});

</script>

<style lang="scss" scoped>
.form-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;
}

.form-field label {
    margin-bottom: 0.5rem;
}
</style>